import cn from "classnames";
import * as React from "react";
import s from "./Stepper.module.scss";
import { Typography } from "../Typography";

import vibrate from "core-lib/utils/vibration";
import Icon from "../Icon/Icon";

enum sizeType {
  small = "small",
  medium = "medium",
}

type sizeMap = keyof typeof sizeType;

export interface StepperProps {
  count: number;
  minLimit?: number;
  maxLimit?: number;
  step?: number;
  onChange(args: number): void;
  size?: sizeMap;
}

/***
 * Stepper -> Used to increment or decrement a value.
 *
 * @param count : required. default value of the stepper
 * @param minLimit : optional. minimum value of the stepper. Default is 0.
 * @param maxLimit : optional. maximum value of the stepper. Default is 20.
 * @param step : optional. step value of the stepper. Default is 1.
 * @param onChange : required. callback function that returns the updated value of the stepper.
 * @param size : optional. size of the stepper. Default is medium.
 */

const Stepper: React.FunctionComponent<StepperProps> = ({
  count = 5,
  minLimit = 0,
  maxLimit = 20,
  step = 1,
  size = sizeType.medium,
  onChange,
}) => (
  <div className={s.root}>
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (count - step < minLimit) {
          return;
        }
        vibrate();
        onChange(count - step);
      }}
      className={cn(s.btn, s[size], {
        [s.disabled]: count - step < minLimit,
      })}
    >
      <Icon
        name="MinuRegular"
        width={size === sizeType.small ? 16 : 20}
        height={size === sizeType.small ? 16 : 20}
      />
    </button>
    <div className={s.text}>
      <Typography color="brand" variant="label_1_bold" label={count} />
    </div>
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (count + step > maxLimit) {
          return;
        }
        vibrate();
        onChange(count + step);
      }}
      className={cn(s.btn, s[size], {
        [s.disabled]: count + step > maxLimit,
      })}
    >
      <Icon
        name="PlusRegular"
        width={size === sizeType.small ? 16 : 20}
        height={size === sizeType.small ? 16 : 20}
      />
    </button>
  </div>
);

export default Stepper;
